import {
  AnchorButton,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  PopoverInteractionKind,
  Position,
  Switch,
  Tag,
  Tooltip
} from '@blueprintjs/core'
import { IconSvgPaths16 } from '@blueprintjs/icons'
import cx from 'classnames'
import { BotConfig, ModuleDefinition } from 'enablex/sdk'
import { lang, utils, toast } from 'enablex/shared'
import { intersection } from 'lodash'
import moment from 'moment'
import React, { FC, useEffect, useRef, useState } from 'react'
import api from '~/app/api'
import { history } from '~/app/store'
import AccessControl, { isChatUser, isOperationAllowed } from '~/auth/AccessControl'
import AnalyticsIcon from '~/icons/AnalyticsIcon'
import ChatIcon from '~/icons/ChatIcon'
import CodeSnippetIcon from '~/icons/CodeSnippetIcon'
import EditIcon from '~/icons/EditIcon'
import ExportIcon from '~/icons/ExportIcon'
import LiveChatIcon from '~/icons/LiveChatIcon'
import { LogIcon } from '~/icons/LogIcon'
import ResetIcon from '~/icons/ResetIcon'
import SettingsIcon from '~/icons/SettingsIcon'
import TrashIcon from '~/icons/TrashIcon'
import UserIcon from '~/icons/UserIcon'
import NotificationIcon from '~/icons/NotificationIcon'
import ErrorIcon from '~/icons/ErrorIcon'
import VerticalEllipses from '~/icons/VerticalEllipses'
import SessionIcon from '~/icons/SessionIcon'
import { AgentsListView } from './AgentsListView'
import { NeedsTrainingWarning } from './NeedsTrainingWarning'
import style from './style.scss'
import LanguageIcon from '~/icons/LanguageIcon'
import EventBus from '~/app/EventBus'

interface Props {
  bot: BotConfig
  email?: string
  hasError: boolean
  installedNLULanguages: string[]
  loadedModules: ModuleDefinition[]
  deleteBot?: () => void
  exportBot?: () => void
  createRevision?: () => void
  rollback?: () => void
  deployment?: () => void
  reloadBot?: () => void
  viewLogs?: () => void
  profile?: any // Add profile prop to access current user information
}

const BotItemCompact: FC<Props> = props => {
  const botShortLink = `${window.location.origin + window['ROOT_PATH']}/s/${props.bot.id}`
  const botStudioLink = isChatUser() ? botShortLink : `studio/${props.bot.id}`
  const liveConvLink = `${window.location.origin + window['ROOT_PATH']}/admin/apps/hitlnext/${props.bot.id}`
  const nluModuleEnabled = !!props.loadedModules.find(m => m.name === 'nlu')
  const hasStudioAccess = isOperationAllowed({ resource: 'studio', operation: 'read' })
  const languages = intersection(props.bot.languages, props.installedNLULanguages)
  const botHasUninstalledNLULanguages = props.bot.languages.length !== languages.length
  const currentRole = window.BP_STORAGE.get('enx/channel-web/user-role') || ''
  const isAgent = currentRole === 'agent'
  const isAdmin = !(currentRole === 'agent' || currentRole === 'dev')
  const isSuperAdmin = currentRole === 'Super Admin'
  const isSupervisor = currentRole === 'supervisor'
  const isLiveChat = !(currentRole === 'agent' || currentRole === 'dev' || currentRole === 'Super Admin')
  const [linkedAgents, setlinkedAgents] = useState<any[]>([])
  const linkedAgentsRef = useRef(linkedAgents)
  const [openTicketCount, setOpenTicketCount] = useState(0)
  const [assignedTicketCount, setAssignedTicketCount] = useState(0)
  const [isAgentListOpen, setAgentListOpen] = useState(false)
  const toggleAgentView = () => setAgentListOpen(!isAgentListOpen)
  const [isPublished, setIsPublished] = useState(!props.bot.private)
  const isToastrVisible= true
  const togglePublishButton = async () => {
    try {
      const updatedIsPublished = !isPublished
      setIsPublished(updatedIsPublished)
      const axios = api.getSecured({ useV1: false })
      await axios.post(`admin/workspace/bots/${props.bot.id}/published`, { published: updatedIsPublished })
    } catch (error) {}
  }
  const updateAgents = updatedAgentsList => {
    setlinkedAgents(updatedAgentsList)
  }

  async function updateTicketCount() {
    try {
      const axios = api.getSecured({ useV1: true })
      const { data } = await axios.get(`bots/${props.bot.id}/mod/hitlnext/handoffs`)
      if (data) {
        const agents: any[] = linkedAgents.length ? linkedAgents : linkedAgentsRef.current
        const currentAgent: any | undefined = agents.find(item => item?.agentId === props?.email)
        const labels = currentAgent?.agentSkill || []
        //const agentLabel = typeof labels == 'string' ? JSON.parse(labels) : labels
        const agentLabel = labels
        const handoffs = agentLabel.length > 0
        ? data.filter(item =>
            agentLabel.some(skill => item.tags.includes(skill)) || item.tags.length === 0
          )
        : data
        const assignedHandoffs = handoffs.filter(item => (item.status === 'assigned' && item.agentEmail === currentAgent.agentId))
        setAssignedTicketCount(assignedHandoffs.length)
        const pendingHandoffs = handoffs.filter(item => item.status === 'pending')
        setOpenTicketCount(pendingHandoffs.length)
      } else {
        setAssignedTicketCount(0)
        setOpenTicketCount(0)
      }
    } catch (error) {
      setlinkedAgents([])
    }
  }

  function showNotification(title, body, icon) {
    if(document.visibilityState === 'visible') {
        return
    }
    let notification = new Notification(title, { body, icon })
    notification.onclick = () => {
      notification.close()
      window.parent.focus()
    }
  }
  function requestAndShowNotification(title, body, icon, toastType) {
    if (typeof Notification !== 'undefined') {
      let permission = Notification.permission
      if (permission === 'default') {
        void Notification.requestPermission(function (permission) {
          if (permission === 'granted') {
            showNotification(title, body, icon)
          }
        })
      } else if (permission === 'granted') {
        showNotification(title, body, icon)
      }
    }
    //Show toastr
    if (isToastrVisible) {
      if (toastType === 'error') {
        toast.failure(<span style={{fontWeight: 'bold'}}>{body}</span>, title, {icon: <ErrorIcon />, timeout: 'medium', key: title,toasterProps: {position: Position.TOP_RIGHT}})
      } else if(toastType === 'info') {
        toast.info(<span style={{fontWeight: 'bold'}}>{body}</span>, title, {icon: <NotificationIcon />, timeout: 'medium',key: title, toasterProps: {position: Position.TOP_RIGHT}})
      }
    }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const axios = api.getSecured({ useV1: false })
        const { data } = await axios.get(`admin/workspace/bots/${props.bot.id}/getAgentsDataForBot`)
        if (data.payload) {
          setlinkedAgents(data.payload)
        } else {
          setlinkedAgents([])
        }
      } catch (error) {
        setlinkedAgents([])
      }
      // await updateTicketCount()
    }

    void fetchData()
  }, [props.bot.id])

  useEffect(() => {
    async function updateCount() {
      linkedAgentsRef.current = linkedAgents
      await updateTicketCount()
    }

    if (linkedAgents.length > 0) {
      void updateCount()
    }
  }, [linkedAgents])

  useEffect(() => {
    const configUpdated = async (event) => {
      // console.log('event:: ', JSON.parse(JSON.stringify(event)))
      if (event.type === 'create') {
        const agents: any[] = linkedAgentsRef.current
        const currentAgent = agents.find(item => item?.agentId === props?.email)
        const labels = currentAgent?.agentSkill || []
        //const agentLabel = typeof labels == 'string' ? JSON.parse(labels) : labels
        const agentLabel = labels
        const allow = agentLabel.length > 0
        ? (event.payload.tags.some(tag => agentLabel.includes(tag)) || event.payload.tags.length === 0)
        : true
        if (allow) {
          requestAndShowNotification('Request created', `New request created for bot: ${props.bot?.name}`, props.bot?.details?.avatarUrl, 'info')
        }
      } else if (event.type === 'update') {
        const agents: any[] = linkedAgentsRef.current
        const currentAgent = agents.find(item => item?.agentId === props?.email)
        const labels = currentAgent?.agentSkill || []
        //const agentLabel = typeof labels == 'string' ? JSON.parse(labels) : labels
        const agentLabel = labels
        const allow = agentLabel.length > 0
        ? (event.payload.tags.some(tag => agentLabel.includes(tag)) || event.payload.tags.length === 0)
        : true
        if (allow) {
          if(event.payload?.status === 'expired') {
            requestAndShowNotification('Request expired', `Request expired for bot: ${props.bot?.name}`, props.bot?.details?.avatarUrl, 'error')
          } else if(event.payload?.autoAssigned && currentAgent.agentId === event.payload?.agentEmail) {
            requestAndShowNotification('Request assigned', `Request assigned for bot: ${props.bot?.name}`, props.bot?.details?.avatarUrl, 'info')
          } else if(event.payload?.status === 'disconnected') {
            requestAndShowNotification('Request disconnected', `User disconnected for bot: ${props.bot?.name}`, props.bot?.details?.avatarUrl, 'error')
          }
        }
      }
      await updateTicketCount()
    }

    if (window.localStorage.getItem('enx/channel-web/user-role') === 'agent') {
      EventBus.default.on(`hitlnext:${props.bot.id}`, configUpdated)
    }
    return () => {
      if (window.localStorage.getItem('enx/channel-web/user-role') === 'agent') {
        EventBus.default.off(`hitlnext:${props.bot.id}`, configUpdated)
      }
    }
  }, [])

  // Self-heal the per-bot badge count. Realtime 'hitlnext:<botId>' events can be
  // missed for a bot (e.g. an 'expired' update not delivered), which left the
  // "open" badge stale or inconsistent between bots. Re-count from the server on
  // a light interval and whenever the tab/window regains focus, so the badge
  // always reflects the real pending count regardless of missed events.
  useEffect(() => {
    if (window.localStorage.getItem('enx/channel-web/user-role') !== 'agent') {
      return
    }
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        void updateTicketCount()
      }
    }
    const intervalId = setInterval(() => {
      void updateTicketCount()
    }, 30000)
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  useEffect(() => {}, [isAgentListOpen])

  const modules = props.loadedModules
    .filter(x => x.workspaceApp?.bots)
    .filter(module => isOperationAllowed({ resource: `module.${module.name}`, operation: 'write' }))
  const addModuleIcon = module => {
    const iconPath = `assets/modules/${module.name}/admin_${module.menuIcon}`
    const moduleIcon =
      module.menuIcon && IconSvgPaths16[module.menuIcon] ? (
        <Icon icon={module.menuIcon as any} iconSize={32} />
      ) : (
        <img src={iconPath} />
      )
    return { ...module, menuIcon: moduleIcon }
  }

  const branding = utils.getBranding()
  const timeFormat = 'DD/MM/YY hh:mm A'
  return (
    <div
      className={cx('bp_table-row', style.tableRow, props.bot.disabled ? `${style.botDisabled} bot-disabled` : '')}
      key={props.bot.id}
      style={isAgent ? {
        cursor: 'pointer'
      } : {}}
      onClick={() => {
        if(isAgent) {
          window.location.href = liveConvLink
        }
      }}
    >
      {/* Title */}
      <div className={style.title}>
        <div className={style.titleLeft}>
          <div className={style.titleAndBadges}>
            {props.bot.locked && (
              <div>
                <Icon icon="lock" intent={Intent.PRIMARY} iconSize={13} />
              </div>
            )}

            {hasStudioAccess && !props.bot.disabled ? (
              <a href={botStudioLink}>{props.bot.name || props.bot.id}</a>
            ) : isSupervisor ? (
              // Allow supervisors to access hitlnext app for bots
              <a href={liveConvLink}>{props.bot.name || props.bot.id}</a>
            ) : (
              <a href={'#'} style={{ pointerEvents: 'none' }}>
                {props.bot.name || props.bot.id}
              </a>
            )}
            {
              isAgent &&
              <div style={{width: 'fit-content', display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '8px', gap: '4px'}}>
                {
                  assignedTicketCount ?
                    <Tooltip
                      position="top"
                      content={'Assigned Handovers'}
                    >
                      <Tag intent={Intent.PRIMARY} round={true}>
                        {assignedTicketCount}
                      </Tag>
                    </Tooltip> : <></>
                }
            {
              openTicketCount ?
                <Tooltip
                  position="top"
                  content={'Open Handovers'}
                >
                  <Tag intent={Intent.WARNING} round={true}>
                    {openTicketCount}
                  </Tag>
                </Tooltip> : <></>
            }
              </div>
            }
            <AccessControl resource="module.nlu" operation="write">
              {nluModuleEnabled && !props.bot.disabled && (
                <NeedsTrainingWarning bot={props.bot.id} languages={props.bot.languages} isPublished={isPublished}/>
              )}
            </AccessControl>

            {botHasUninstalledNLULanguages && !isAgent && (
              <Tooltip
                position="right"
                content={lang.tr('admin.workspace.bots.item.enableNLULanguages', {
                  languages: languages.join(',')
                })}
              >
                <Icon icon={<LanguageIcon color={branding?.themeColor || '#1d6c90'} />} intent={Intent.DANGER} style={{ marginLeft: 10 }} />
              </Tooltip>
            )}

            {props.bot.disabled && (
              <Tag intent={Intent.WARNING} className={style.botbadge}>
                Disabled
              </Tag>
            )}
          </div>
          <span style={{ fontSize: '14px', marginTop: '5px' }}>
            Webchat
            {props.bot.details?.isVoiceBotEnabled ? ', Voice Bot' : ''}
            {props.bot.details?.isWhatsappEnabled ? ', WhatsApp' : ''}
            {props.bot.details?.isRcsEnabled ? ', RCS' : ''}
            {props.bot.details?.isFacebookEnabled ? ', Messenger' : ''}
            {props.bot.details?.isInstagramEnabled ? ', Instagram' : ''}
            {props.bot.details?.isTelegramEnabled ? ', Telegram' : ''}
            {props.bot.details?.isVideoEnabled ? ', Video Call' : ''}
            {props.bot.details?.isAudioEnabled ? ', Phone Call' : ''}
          </span>
          <span style={{ fontSize: '12px', marginTop: '5px', fontWeight: 'normal' }}>
            {props.bot.creationDateUtc ? (
              <span>
                {' '}
                <strong> Created: </strong> {moment(props.bot.creationDateUtc).format(timeFormat)} {' '}
              </span>
            ) : (
              ''
            )}
          </span>
          {/* {
            isAgent &&
            (<div style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}>
              <span style={{ fontSize: '12px', marginTop: '5px' }}>
                Assigned:&nbsp;&nbsp;{assignedTicketCount}
                </span>
              <span style={{ fontSize: '12px', marginTop: '5px' }}>
                Open:&nbsp;&nbsp;&nbsp;&nbsp;{openTicketCount}
              </span>
            </div>)
          } */}
        </div>

        {/* Publish Switch */}
        {!isAgent && (
          <Switch
            checked={isPublished && !props.bot.disabled}
            onChange={e => togglePublishButton()}
            innerLabelChecked="Published"
            innerLabel="Drafted"
            className={cx('bot-status-switch', style.botStatusSwitch)}
            large={true}
            disabled={props.bot.disabled || isSupervisor}
          />
        )}
        {isAgent && (
          <div className={cx('agent-actions', style.actions)}>
            <div
              className={'agent-actions-right'}
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            >
              {props.hasError && (
                <AnchorButton
                  text={lang.tr('admin.workspace.bots.item.reload')}
                  icon={<ResetIcon />}
                  onClick={props.reloadBot}
                  minimal
                />
              )}
            </div>
          </div>
        )}
      </div>
      {/* End Title */}

      {/* Divider */}
      {!isAgent && <div className={cx('actions-divider', style.actionsDivider)}></div>}
      {/* End Divider */}

      {/* Actions */}
      {!isAgent && (
        <div className={cx('actions', style.actions)}>
          <div className={'actions-left'}>
            {!isAgent && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1d6c90'
                }}
              >
                <AnchorButton
                  icon={<UserIcon color={'#333'} strokeWidth={'2'} />}
                  onClick={toggleAgentView}
                  disabled={props.bot.disabled}
                  minimal
                  text={`Agents (${linkedAgents.length})`}
                />
              </div>
            )}
          </div>
          <div className={'actions-right'} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {(isSupervisor || isAgent) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '12px' }}>
                <AnchorButton
                  icon={<AnalyticsIcon />}
                  disabled={props.bot.disabled}
                  href={`/admin/apps/hitlnextsessions/${props.bot.id}/sessions`}
                  minimal
                  style={{ fontSize: '16px' }}
                  text={'Sessions'}
                />
              </div>
            )}
            {props.hasError && (
              <AnchorButton
                text={lang.tr('admin.workspace.bots.item.reload')}
                icon={<ResetIcon />}
                onClick={props.reloadBot}
                disabled={props.bot.disabled}
                minimal
              />
            )}
            {!isAgent && !isSupervisor && (
              <AccessControl resource="admin.bots.*" operation="write">
                {
                  <AnchorButton
                    text={lang.tr('admin.workspace.bots.item.config')}
                    icon={<SettingsIcon />}
                    disabled={props.bot.disabled}
                    minimal
                    onClick={() => (location.href = `${botStudioLink}/config`)}
                  />
                }
              </AccessControl>
            )}

            {/* {isPublished && !props.bot.disabled && !props.hasError && !isAgent && (
              <AnchorButton
                text={lang.tr('admin.workspace.bots.item.openChat')}
                icon={<ChatIcon />}
                href={botShortLink}
                target="_blank"
                minimal
              />
            )} */}
            {!isPublished && !isAgent && !isSupervisor && (
              <AnchorButton
                text={lang.tr('admin.workspace.bots.item.deployment')}
                icon={<CodeSnippetIcon />}
                id="btn-deploy-bot-item"
                disabled={props.bot.disabled}
                onClick={props.deployment}
                minimal
              />
            )}
            {!isPublished && !props.bot.disabled && !props.hasError && hasStudioAccess && (
              <AnchorButton
                disabled={props.bot.locked}
                icon={<EditIcon />}
                text={lang.tr('admin.workspace.bots.item.editInStudio')}
                href={botStudioLink}
                minimal
              />
            )}
            {isPublished && !isAgent && !isSupervisor && (
              <AnchorButton
                id="btn-menu-analytics"
                key="analytics"
                text={lang.tr('module.analytics.fullName') || 'Analytics'}
                icon={<AnalyticsIcon />}
                disabled={props.bot.disabled}
                onClick={() => (location.href = `${botStudioLink}/modules/analytics`)}
                // onClick={() => history.push(`/apps/analytics/${props.bot.id}`)}
                // resource="module.analytics"
                minimal
              />
            )}
            {!props.bot.disabled && !props.hasError && isAgent && !isSupervisor && (
              <AnchorButton
                text={lang.tr('admin.workspace.bots.item.LiveConv')}
                icon={<ChatIcon />}
                disabled={props.bot.disabled}
                href={liveConvLink}
                minimal
              />
            )}
            {/* {isSupervisor && (
              <AnchorButton
                text={lang.tr('admin.workspace.bots.item.sessions')}
                icon={<SessionIcon />}
                disabled={props.bot.disabled}
                href={`${botStudioLink}/modules/sessions`}
                minimal
              />
            )} */}
            {/* right side menu */}
            {!isAgent && !isSupervisor && (
              <AccessControl resource="admin.bots.*" operation="read">
                <Popover minimal position={Position.BOTTOM} interactionKind={PopoverInteractionKind.HOVER}>
                  <AnchorButton
                    className="btn-menu-bot-item"
                    icon={<VerticalEllipses />}
                    disabled={props.bot.disabled}
                    minimal
                  />
                  <Menu className={props.bot.disabled ? style.disabledMenu : ''}>
                    {isPublished && !props.bot.disabled && !props.hasError && hasStudioAccess && (
                      <MenuItem
                        disabled={props.bot.locked}
                        icon={<EditIcon />}
                        text={lang.tr('admin.workspace.bots.item.editInStudio')}
                        href={botStudioLink}
                      />
                    )}
                    {/* <AccessControl resource="admin.logs" operation="read">
                      {isSuperAdmin && (
                        <MenuItem
                          text={lang.tr('admin.workspace.bots.item.viewLogs')}
                          icon={<LogIcon />}
                          id="btn-viewLogs"
                          onClick={props.viewLogs}
                        />
                      )}
                    </AccessControl> */}
                    {modules.length > 0 &&
                      modules
                        .filter(module => !(module.name === 'hitlnext' && !isLiveChat))
                        .map(addModuleIcon)
                        .map(module =>
                          module.name === 'analytics' ? (
                            !isPublished && (
                              <MenuItem
                                id={`btn-menu-${module.name}`}
                                key={module.name}
                                text={module.fullName || module.menuText}
                                // text={lang.tr(`module.${module.name}.fullName`) || module.menuText}
                                icon={<AnalyticsIcon />}
                                onClick={() => history.push(`/apps/${module.name}/${props.bot.id}`)}
                                resource={`module.${module.name}`}
                              />
                            )
                          ) : module.name === 'hitlnext' ? (
                            !isSupervisor && (
                              <MenuItem
                                id={`btn-menu-${module.name}`}
                                key={module.name}
                                // text={lang.tr(`module.${module.name}.fullName`) || module.menuText}
                                text={module.menuText}
                                icon={<LiveChatIcon />}
                                onClick={() => history.push(`/apps/${module.name}/${props.bot.id}`)}
                                resource={`module.${module.name}`}
                              />
                            )
                          ) : (
                            <MenuItem
                              id={`btn-menu-${module.name}`}
                              key={module.name}
                              // text={lang.tr(`module.${module.name}.fullName`) || module.menuText}
                              text={module.menuText}
                              icon={module.menuIcon as any}
                              onClick={() => history.push(`/apps/${module.name}/${props.bot.id}`)}
                              resource={`module.${module.name}`}
                            />
                          )
                        )}
                    <AccessControl resource="admin.bots.*" operation="write">
                      {isPublished && !isAgent && (
                        <MenuItem
                          text={lang.tr('admin.workspace.bots.item.deployment')}
                          icon={<CodeSnippetIcon />}
                          id="btn-deploy-bot-item"
                          onClick={props.deployment}
                        />
                      )}
                    </AccessControl>
                    <AccessControl resource="admin.bots.archive" operation="read">
                    {isAdmin && (
                      <MenuItem
                        text={lang.tr('admin.workspace.bots.item.export')}
                        icon={<ExportIcon />}
                        id="btn-export-bot-item"
                        onClick={props.exportBot}
                      />
                      )}
                    </AccessControl>
                    <AccessControl resource="admin.bots.*" operation="write">
                      {isAdmin && (
                        <MenuItem
                          text={lang.tr('admin.workspace.bots.item.delete')}
                          icon={<TrashIcon />}
                          id="btn-delete-bot-item"
                          onClick={props.deleteBot}
                        />
                      )}
                    </AccessControl>
                  </Menu>
                </Popover>
              </AccessControl>
            )}
          </div>
        </div>
      )}
      {/* End Actions */}

      <AgentsListView
        isOpen={isAgentListOpen}
        toggle={toggleAgentView}
        onUpdate={updateAgents}
        botConfig={props.bot}
        linkedAgents={linkedAgents}
        profile={props.profile}
      />
    </div>
  )
}
export default BotItemCompact
